import { css } from '@emotion/react/macro';
import styled from '@emotion/styled/macro';
import { DataTable } from '../../data-table';
import { TitleCard } from '../../card';
import { Variables } from '../../base';
import { Icon } from '@mdi/react';
import MarkdownWrapper from '@enter/core/src/ui/base/markdown-wrapper/markdown-wrapper';

/**
 * Consolidated styles for ClaimTimeline component
 * Merged from Provider's claim-timeline-table.styles.tsx and Support's timeline.styles.ts
 */

// ============================================================================
// Common Interfaces
// ============================================================================

interface ISelectableProps {
    selectable: boolean;
}

interface IAlign {
    align?: 'left' | 'right';
}

interface IExpandParent {
    expand?: boolean;
}

// ============================================================================
// Common Styles
// ============================================================================

const hoverable = css`
    &:hover {
        background-color: ${Variables.Colors.n5};
    }
`;

const selectableStyle = (props: ISelectableProps) => css`
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: ${props.selectable ? 'pointer' : 'default'};
`;

// ============================================================================
// Wrapper Components (Provider uses TitleCard, Support uses TableWrapper)
// ============================================================================

export const StyledTitleCard = styled(TitleCard)`
    & > section > section:first-of-type {
        display: none;
    }
`;

export const TableWrapper = styled.section`
    border-radius: 3px;
    box-shadow: ${Variables.Shadows.small}, ${Variables.Shadows.border};
    background-color: ${Variables.Colors.white};

    div[role='row'] {
        [role='cell']:first-of-type {
            padding: 0;
        }
    }
`;

export const PaymentsToggleRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 0 20px 0 0;
`;

// ============================================================================
// Data Table Styles
// ============================================================================

export const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    margin: 12px 0;
    min-height: 200px;

    & > div {
        overflow-y: hidden;
    }

    /* Override DataTable's internal table overflow */
    div[role='table'] {
        overflow-x: visible;
        overflow-y: visible;

        & > div:first-of-type {
            [role='columnheader'] {
                white-space: nowrap;
            }
        }
    }

    div[role='row'] {
        ${hoverable}
        height: auto;
    }

    div[role='columnheader'] {
        padding: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: ${Variables.ThemeLight.TextMedium};
    }

    div[role='cell'] {
        color: ${Variables.ThemeLight.TextMedium};
    }

    div[role='row'] {
        [role='cell']:first-of-type {
            padding: 0;
            align-items: stretch;
        }
    }
`;

// ============================================================================
// Cell Components
// ============================================================================

export const SelectableCell = styled.span<ISelectableProps>`
    ${selectableStyle};
    display: block;
`;

export const StyledSegment = styled(SelectableCell)`
    ${selectableStyle};
    padding: 0;
    display: flex;
    align-items: stretch;
    justify-content: center;
    width: 100%;
    height: 100%;
    align-self: stretch;
`;

export const StyledDescription = styled(SelectableCell)`
    color: ${Variables.Colors.n100};
    width: 100%;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
`;

export const Description = styled.span`
    display: flex;
    align-items: center;
    width: 100%;
`;

export const DescriptionItem = styled.span`
    &:not(:first-of-type) {
        margin-left: 8px;
    }
`;

// ============================================================================
// Event Display Components
// ============================================================================

export const StyledEvent = styled.span`
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;

    &:not(:first-of-type) {
        margin-left: 4px;
    }
`;

// Support's Event component (with icon)
export const Event = styled.div`
    display: flex;
    align-items: center;
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;
`;

export const EventLabel = styled.div``;

export const SecondaryEvent = styled.div`
    display: flex;
    align-items: center;
    margin-left: 5px;
`;

// ============================================================================
// Icon Components
// ============================================================================

export const StyledIcon = styled(Icon)`
    margin-top: 4px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.b300};
    align-self: center;
`;

export const EventIcon = styled(Icon)`
    fill: ${Variables.Colors.n70};
    text-align: center;
    margin-right: 5px;
`;

export const ExpandIcon = styled(Icon)`
    fill: ${Variables.Colors.b300};
`;

export const AttachIcon = styled(Icon)`
    transform: rotate(45deg);
    margin-right: 4px;
    fill: ${Variables.Colors.n30};
`;

export const DocumentIcon = styled(Icon)`
    height: 64px;
    width: 64px;
    fill: ${Variables.Colors.n50};
    margin-right: 32px;
    cursor: pointer;
`;

// ============================================================================
// Expandable Row Components
// ============================================================================

export const ExpandRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    padding: 0;
    width: 100%;
`;

export const ExpandSegment = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 50px;
`;

export const SegmentContainer = styled.section`
    min-width: 50px;
`;

export const SubRowContainer = styled.section`
    display: flex;
    width: 100%;
`;

export const ExpandCell = styled.div`
    display: flex;
    flex-grow: 1;
    padding: 16px 0px;
    width: 100%;
    min-width: 0;
`;

export const ExpandCol = styled.div`
    width: 100%;
`;

export const ExpandWrap = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
    width: 100%;
    min-width: 0;
`;

export const ExpandContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
`;

// ============================================================================
// Expand Table Components (Provider-specific detail tables)
// ============================================================================

export const ExpandTable = styled.table<IExpandParent>`
    white-space: normal;
    color: ${Variables.Colors.n100};

    ${({ expand }) =>
        expand
            ? css`
                  margin: 0;
              `
            : css`
                  min-width: 40%;
              `}
`;

export const ExpandTD = styled.td<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
`;

export const ExpandTDCommentBody = styled(ExpandTD)`
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
`;

export const ExpandTH = styled.th<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
    vertical-align: top;
    font-weight: 500;
`;

export const ExpandTR = styled.tr`
    & ${ExpandTD}, & ${ExpandTH} {
        font-size: 16px;
        font-weight: 400;
        letter-spacing: 0.5px;
        padding: 4px 8px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const ExpandTHead = styled.thead`
    ${ExpandTH} {
        font-size: 20px;
        padding-bottom: 16px;
    }
`;

export const ExpandTBody = styled.tbody``;

export const ExpandTFoot = styled.tfoot``;

export const ExpandSubTH = styled(ExpandTH)``;

export const ExpandTitleTH = styled(ExpandTH)``;

export const ExpandTitle = styled.h3`
    margin: 18px 0 0 0;
    font-weight: 500;
    font-size: 16px;
    text-decoration: underline;
`;

export const ExpandListContainer = styled.div`
    width: 100%;
    margin-top: 16px;
`;

export const ExpandListItem = styled.div`
    margin-bottom: 16px;
    &:last-of-type {
        margin-bottom: 0;
    }
`;

export const ExpandSubtitle = styled.div`
    font-weight: 500;
    margin-bottom: 4px;
`;

export const ExpandLabel = styled.div`
    font-weight: 500;
    padding: 5px 8px;
`;

// ============================================================================
// Utility Components
// ============================================================================

export const StyledLink = styled.a`
    color: ${Variables.Colors.p300};
    text-decoration: underline;
`;

export const Attachment = styled.span`
    display: inline-flex;
    align-items: center;
`;

export const MarkdownBlock = styled(MarkdownWrapper)`
    margin: 0;
`;

export const ClaimBundleWrapper = styled.div`
    display: flex;
    padding-left: 16px;
    margin: 10px;
`;

export const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: ${Variables.Colors.platinum};
    margin: 16px 0;
    position: relative;
`;

export const FilterSelectContainer = styled.div`
    padding: 20px;
`;

export const RowGroupWrapper = styled.div`
    text-align: center;
    background: ${Variables.Colors.n15_48};
    padding: 10px 0;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid ${Variables.Colors.n15};
    border-bottom: 1px solid ${Variables.Colors.n15};

    &:hover {
        background: ${Variables.Colors.n15_16};
    }
`;

export const RowWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export const Wrapper = styled.div`
    width: 100%;
`;

export const CommentBodyContainer = styled.div`
    max-width: 100%;
`;

// ============================================================================
// Detail Components (Provider-specific)
// ============================================================================

export const DetailBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 16px;
`;

export const DetailLabel = styled.div`
    font-weight: 500;
    color: ${Variables.Colors.n100};
`;

export const DetailValue = styled.div`
    color: ${Variables.Colors.n100};
    word-break: break-word;
`;

export const ExpandDetailButton = styled.button`
    margin: 10px 0;
    padding: 6px 12px;
    cursor: pointer;
`;

export const ExpandIframe = styled.iframe`
    padding: 10px 15px;
    width: 100%;
    min-height: 200px;
    overflow-x: auto;
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.small};
`;

export const PayloadWrapper = styled.div`
    width: 100%;
`;

export const JsonViewersContainer = styled.div`
    display: flex;
    width: 100%;
    align-items: flex-start;
    overflow: hidden;
`;

export const JsonViewerColumn = styled.div`
    flex: 1;
    min-width: 0;
    max-width: 33.33%;
`;

export const JsonViewerLabel = styled.div`
    font-weight: 500;
    margin-bottom: 8px;
`;

export const JsonViewerWrapper = styled.div`
    & > div {
        font-size: 14px;
    }
`;

export const JsonViewToolbar = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
`;

// ============================================================================
// Re-exported from shared timeline styles
// ============================================================================

export const TablesGroup = styled.div`
    width: 100%;
    display: flex;
    > div {
        width: 50%;
        &:not(:first-of-type) {
            margin-left: 40px;
        }
    }
`;

export const ScrollableCodeContainer = styled.div`
    max-height: none;
    overflow: visible;
    border-radius: ${Variables.Rounded.small};
    padding: 8px;
    background-color: transparent;
    box-sizing: border-box;
    margin: 0 10px;

    textarea {
        overflow: hidden !important;
        max-height: none !important;
        height: auto !important;
    }
`;
export const SecondaryIcon = styled(Icon)`
    fill: ${Variables.Colors.n70};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: 6px;
    background: ${Variables.Colors.white};
`;

export const FlexWrapper = styled.div`
    display: flex;
`;

export const Subtitle = styled.div`
    font-size: 16px;
    font-weight: 600;
`;

export const ResourceTable = styled.section`
    width: 100%;
    padding: 10px;
    box-shadow: ${Variables.Shadows.border};
`;
